import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import CryptoJS from 'crypto-js';
import { DomainKey } from '../config/domains';
import { colorSchemes, defaultColorScheme, ColorScheme } from '../theme/theme';
import { PresenceStatus } from '@actschurch/ccms-sdk';

const SECRET = import.meta.env.VITE_CLIENT_SECRET || 'dev-secret-key';

// Encryption helpers for persistence
const encryptedStorage = {
  getItem: (name: string): string | null => {
    const cipher = localStorage.getItem(name);
    if (!cipher) return null;
    try {
      const bytes = CryptoJS.AES.decrypt(cipher, SECRET);
      return bytes.toString(CryptoJS.enc.Utf8);
    } catch {
      return null;
    }
  },
  setItem: (name: string, value: string): void => {
    const cipher = CryptoJS.AES.encrypt(value, SECRET).toString();
    localStorage.setItem(name, cipher);
  },
  removeItem: (name: string): void => {
    localStorage.removeItem(name);
  },
};

export type ColorSchemeMode = 'light' | 'dark' | 'auto';

export interface Language {
  code: string;
  label: string;
  flag: string;
  direction: 'ltr' | 'rtl';
}

export const SUPPORTED_LANGUAGES: Language[] = [
  { code: 'en', label: 'English', flag: '🇺🇸', direction: 'ltr' },
  { code: 'es', label: 'Espa\u00f1ol', flag: '🇪🇸', direction: 'ltr' },
  { code: 'fr', label: 'Fran\u00e7ais', flag: '🇫🇷', direction: 'ltr' },
  { code: 'de', label: 'Deutsch', flag: '🇩🇪', direction: 'ltr' },
  { code: 'pt', label: 'Portugu\u00eas', flag: '🇧🇷', direction: 'ltr' },
  { code: 'it', label: 'Italiano', flag: '🇮🇹', direction: 'ltr' },
  { code: 'zh', label: '\u4e2d\u6587', flag: '🇨🇳', direction: 'ltr' },
  { code: 'ja', label: '\u65e5\u672c\u8a9e', flag: '🇯🇵', direction: 'ltr' },
  { code: 'ko', label: '\ud55c\uad6d\uc5b4', flag: '🇰🇷', direction: 'ltr' },
  { code: 'ar', label: '\u0627\u0644\u0639\u0631\u0628\u064a\u0629', flag: '🇸🇦', direction: 'rtl' },
  { code: 'hi', label: '\u0939\u093f\u0928\u094d\u0926\u0940', flag: '🇮🇳', direction: 'ltr' },
  { code: 'ru', label: '\u0420\u0443\u0441\u0441\u043a\u0438\u0439', flag: '🇷🇺', direction: 'ltr' },
];

export const TIMEZONES = [
  // UTC-12 to UTC-10
  { value: 'Etc/GMT+12', label: '(UTC-12:00) International Date Line West' },
  { value: 'Pacific/Midway', label: '(UTC-11:00) Coordinated Universal Time-11' },
  { value: 'Pacific/Honolulu', label: '(UTC-10:00) Hawaii' },

  // UTC-9 to UTC-7
  { value: 'America/Anchorage', label: '(UTC-09:00) Alaska' },
  { value: 'America/Los_Angeles', label: '(UTC-08:00) Pacific Time (US & Canada)' },
  { value: 'America/Tijuana', label: '(UTC-08:00) Baja California' },
  { value: 'America/Denver', label: '(UTC-07:00) Mountain Time (US & Canada)' },
  { value: 'America/Phoenix', label: '(UTC-07:00) Arizona' },
  { value: 'America/Chihuahua', label: '(UTC-07:00) Chihuahua, La Paz, Mazatlan' },

  // UTC-6
  { value: 'America/Chicago', label: '(UTC-06:00) Central Time (US & Canada)' },
  { value: 'America/Guatemala', label: '(UTC-06:00) Central America' },
  { value: 'America/Mexico_City', label: '(UTC-06:00) Guadalajara, Mexico City, Monterrey' },
  { value: 'Canada/Saskatchewan', label: '(UTC-06:00) Saskatchewan' },

  // UTC-5
  { value: 'America/New_York', label: '(UTC-05:00) Eastern Time (US & Canada)' },
  { value: 'America/Bogota', label: '(UTC-05:00) Bogota, Lima, Quito' },
  { value: 'America/Indiana/Indianapolis', label: '(UTC-05:00) Indiana (East)' },

  // UTC-4
  { value: 'America/Halifax', label: '(UTC-04:00) Atlantic Time (Canada)' },
  { value: 'America/Caracas', label: '(UTC-04:00) Caracas' },
  { value: 'America/Cuiaba', label: '(UTC-04:00) Cuiaba' },
  { value: 'America/Santiago', label: '(UTC-04:00) Santiago' },
  { value: 'America/Georgetown', label: '(UTC-04:00) Georgetown, La Paz, Manaus' },
  { value: 'America/Asuncion', label: '(UTC-04:00) Asuncion' },

  // UTC-3:30 to UTC-2
  { value: 'America/St_Johns', label: '(UTC-03:30) Newfoundland' },
  { value: 'America/Sao_Paulo', label: '(UTC-03:00) Brasilia' },
  { value: 'America/Argentina/Buenos_Aires', label: '(UTC-03:00) Buenos Aires' },
  { value: 'America/Cayenne', label: '(UTC-03:00) Cayenne, Fortaleza' },
  { value: 'America/Montevideo', label: '(UTC-03:00) Montevideo' },
  { value: 'America/Godthab', label: '(UTC-03:00) Greenland' },
  { value: 'Atlantic/South_Georgia', label: '(UTC-02:00) Mid-Atlantic' },

  // UTC-1 to UTC
  { value: 'Atlantic/Azores', label: '(UTC-01:00) Azores' },
  { value: 'Atlantic/Cape_Verde', label: '(UTC-01:00) Cape Verde Islands' },
  { value: 'UTC', label: '(UTC+00:00) Coordinated Universal Time' },
  { value: 'Europe/London', label: '(UTC+00:00) Dublin, Edinburgh, Lisbon, London' },
  { value: 'Atlantic/Reykjavik', label: '(UTC+00:00) Monrovia, Reykjavik' },
  { value: 'Africa/Casablanca', label: '(UTC+00:00) Casablanca' },

  // UTC+1
  { value: 'Europe/Berlin', label: '(UTC+01:00) Amsterdam, Berlin, Bern, Rome, Stockholm, Vienna' },
  { value: 'Europe/Paris', label: '(UTC+01:00) Brussels, Copenhagen, Madrid, Paris' },
  { value: 'Europe/Belgrade', label: '(UTC+01:00) Belgrade, Bratislava, Budapest, Ljubljana, Prague' },
  { value: 'Europe/Warsaw', label: '(UTC+01:00) Sarajevo, Skopje, Warsaw, Zagreb' },
  { value: 'Africa/Lagos', label: '(UTC+01:00) West Central Africa' },

  // UTC+2
  { value: 'Europe/Helsinki', label: '(UTC+02:00) Helsinki, Kyiv, Riga, Sofia, Tallinn, Vilnius' },
  { value: 'Europe/Athens', label: '(UTC+02:00) Athens, Bucharest' },
  { value: 'Africa/Cairo', label: '(UTC+02:00) Cairo' },
  { value: 'Africa/Johannesburg', label: '(UTC+02:00) Harare, Pretoria' },
  { value: 'Asia/Beirut', label: '(UTC+02:00) Beirut' },
  { value: 'Asia/Jerusalem', label: '(UTC+02:00) Jerusalem' },

  // UTC+3
  { value: 'Europe/Istanbul', label: '(UTC+03:00) Istanbul' },
  { value: 'Europe/Moscow', label: '(UTC+03:00) Moscow, St. Petersburg' },
  { value: 'Asia/Kuwait', label: '(UTC+03:00) Kuwait, Riyadh' },
  { value: 'Africa/Nairobi', label: '(UTC+03:00) Nairobi' },
  { value: 'Asia/Baghdad', label: '(UTC+03:00) Baghdad' },

  // UTC+3:30 to UTC+4
  { value: 'Asia/Tehran', label: '(UTC+03:30) Tehran' },
  { value: 'Asia/Dubai', label: '(UTC+04:00) Abu Dhabi, Muscat' },
  { value: 'Asia/Baku', label: '(UTC+04:00) Baku' },
  { value: 'Asia/Tbilisi', label: '(UTC+04:00) Tbilisi' },
  { value: 'Indian/Mauritius', label: '(UTC+04:00) Port Louis' },
  { value: 'Asia/Yerevan', label: '(UTC+04:00) Yerevan' },

  // UTC+4:30 to UTC+5:30
  { value: 'Asia/Kabul', label: '(UTC+04:30) Kabul' },
  { value: 'Asia/Karachi', label: '(UTC+05:00) Islamabad, Karachi' },
  { value: 'Asia/Tashkent', label: '(UTC+05:00) Tashkent' },
  { value: 'Asia/Kolkata', label: '(UTC+05:30) Chennai, Kolkata, Mumbai, New Delhi' },
  { value: 'Asia/Colombo', label: '(UTC+05:30) Sri Jayawardenepura' },

  // UTC+5:45 to UTC+6:30
  { value: 'Asia/Kathmandu', label: '(UTC+05:45) Kathmandu' },
  { value: 'Asia/Dhaka', label: '(UTC+06:00) Dhaka' },
  { value: 'Asia/Almaty', label: '(UTC+06:00) Astana' },
  { value: 'Asia/Yangon', label: '(UTC+06:30) Yangon (Rangoon)' },

  // UTC+7
  { value: 'Asia/Bangkok', label: '(UTC+07:00) Bangkok, Hanoi, Jakarta' },
  { value: 'Asia/Novosibirsk', label: '(UTC+07:00) Novosibirsk' },
  { value: 'Asia/Krasnoyarsk', label: '(UTC+07:00) Krasnoyarsk' },

  // UTC+8
  { value: 'Asia/Shanghai', label: '(UTC+08:00) Beijing, Chongqing, Hong Kong, Urumqi' },
  { value: 'Asia/Singapore', label: '(UTC+08:00) Kuala Lumpur, Singapore' },
  { value: 'Asia/Taipei', label: '(UTC+08:00) Taipei' },
  { value: 'Australia/Perth', label: '(UTC+08:00) Perth' },
  { value: 'Asia/Ulaanbaatar', label: '(UTC+08:00) Ulaanbaatar' },
  { value: 'Asia/Irkutsk', label: '(UTC+08:00) Irkutsk' },

  // UTC+9
  { value: 'Asia/Tokyo', label: '(UTC+09:00) Osaka, Sapporo, Tokyo' },
  { value: 'Asia/Seoul', label: '(UTC+09:00) Seoul' },
  { value: 'Asia/Yakutsk', label: '(UTC+09:00) Yakutsk' },

  // UTC+9:30 to UTC+10
  { value: 'Australia/Adelaide', label: '(UTC+09:30) Adelaide' },
  { value: 'Australia/Darwin', label: '(UTC+09:30) Darwin' },
  { value: 'Australia/Sydney', label: '(UTC+10:00) Canberra, Melbourne, Sydney' },
  { value: 'Australia/Brisbane', label: '(UTC+10:00) Brisbane' },
  { value: 'Australia/Hobart', label: '(UTC+10:00) Hobart' },
  { value: 'Pacific/Guam', label: '(UTC+10:00) Guam, Port Moresby' },
  { value: 'Asia/Vladivostok', label: '(UTC+10:00) Vladivostok' },

  // UTC+11 to UTC+13
  { value: 'Pacific/Noumea', label: '(UTC+11:00) Solomon Islands, New Caledonia' },
  { value: 'Asia/Magadan', label: '(UTC+11:00) Magadan' },
  { value: 'Pacific/Auckland', label: '(UTC+12:00) Auckland, Wellington' },
  { value: 'Pacific/Fiji', label: '(UTC+12:00) Fiji' },
  { value: 'Asia/Kamchatka', label: '(UTC+12:00) Petropavlovsk-Kamchatsky' },
  { value: 'Pacific/Tongatapu', label: "(UTC+13:00) Nuku'alofa" },
  { value: 'Pacific/Apia', label: '(UTC+13:00) Samoa' },
];

interface PreferencesState {
  // Theme settings
  colorSchemeMode: ColorSchemeMode;
  colorSchemeKey: string;
  accentColor: string;

  // Language settings
  language: string;
  locale: string;

  // Regional settings
  timezone: string;
  dateFormat: string;
  timeFormat: '12h' | '24h';
  daylightSaving: 'auto' | 'on' | 'off';

  // Navigation
  lastDomain?: DomainKey;
  sidebarCollapsed: boolean;
  demoSidebarOpenSections: string[];

  // Menu UI Preferences
  menuIconOnly: boolean;
  menuAutoHide: boolean;
  menuDensity: 'compact' | 'regular' | 'comfortable';
  menuShowSectionCounts: boolean;

  // Layout Preferences
  layoutWidth: 'boxed' | 'full-width';
  layoutPadding: 'xs' | 'sm' | 'md' | 'lg' | 'xl';

  // Presence
  presenceStatus: PresenceStatus;

  // Screen Lock
  screenLocked: boolean;

  // Accessibility
  reducedMotion: boolean;
  highContrast: boolean;
  fontSize: 'xs' | 'sm' | 'regular' | 'lg' | 'xl';
  strongFocus: boolean;
  grayscaleMode: boolean;
  lineSpacing: 'tight' | 'regular' | 'relaxed' | 'loose';
  letterSpacing: 'normal' | 'wide';
  showIconLabels: boolean;
  enableTooltips: boolean;
  textToSpeech: boolean;
  uiSize: 'compact' | 'regular' | 'comfortable' | 'spacious';

  // Sync metadata
  lastSyncTime?: number; // Unix timestamp (seconds) from server
  lastModifiedTime?: number; // Local modification timestamp
}

interface PreferencesActions {
  // Theme actions
  setColorSchemeMode: (mode: ColorSchemeMode) => void;
  setColorScheme: (key: string) => void;
  setAccentColor: (color: string) => void;

  // Language actions
  setLanguage: (lang: string) => void;
  setLocale: (locale: string) => void;

  // Regional actions
  setTimezone: (tz: string) => void;
  setDateFormat: (format: string) => void;
  setTimeFormat: (format: '12h' | '24h') => void;
  setDaylightSaving: (mode: 'auto' | 'on' | 'off') => void;

  // Navigation actions
  setLastDomain: (domain: DomainKey) => void;
  toggleSidebar: () => void;
  setSidebarCollapsed: (collapsed: boolean) => void;
  setDemoSidebarOpenSections: (sections: string[]) => void;
  toggleDemoSidebarSection: (sectionLabel: string) => void;

  // Menu UI actions
  toggleMenuIconOnly: () => void;
  setMenuAutoHide: (autoHide: boolean) => void;
  setMenuDensity: (density: 'compact' | 'regular' | 'comfortable') => void;
  toggleMenuShowSectionCounts: () => void;

  // Layout actions
  setLayoutWidth: (width: 'boxed' | 'full-width') => void;
  setLayoutPadding: (padding: 'xs' | 'sm' | 'md' | 'lg' | 'xl') => void;

  // Presence actions
  setPresenceStatus: (status: PresenceStatus) => void;

  // Screen Lock actions
  setScreenLocked: (locked: boolean) => void;

  // Accessibility actions
  setReducedMotion: (reduced: boolean) => void;
  setHighContrast: (high: boolean) => void;
  setStrongFocus: (strong: boolean) => void;
  setFontSize: (size: 'xs' | 'sm' | 'regular' | 'lg' | 'xl') => void;
  setGrayscaleMode: (enabled: boolean) => void;
  setLineSpacing: (spacing: 'tight' | 'regular' | 'relaxed' | 'loose') => void;
  setLetterSpacing: (spacing: 'normal' | 'wide') => void;
  setShowIconLabels: (show: boolean) => void;
  setEnableTooltips: (enabled: boolean) => void;
  setTextToSpeech: (enabled: boolean) => void;
  setUiSize: (size: 'compact' | 'regular' | 'comfortable' | 'spacious') => void;

  // Utility actions
  resetPreferences: () => void;
  setPreference: <K extends keyof PreferencesState>(key: K, value: PreferencesState[K]) => void;
  updatePreferences: (preferences: Partial<PreferencesState>) => void;

  // Computed getters
  getColorScheme: () => ColorScheme;
  getLanguage: () => Language;
  getEffectiveColorMode: () => 'light' | 'dark';
}

type PreferencesStore = PreferencesState & PreferencesActions;

const DEFAULT_PREFERENCES: PreferencesState = {
  colorSchemeMode: 'auto',
  colorSchemeKey: defaultColorScheme.key,
  accentColor: 'indigo',
  language: 'en',
  locale: 'en-US',
  timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
  dateFormat: 'MM/DD/YYYY',
  timeFormat: '12h',
  daylightSaving: 'auto',
  lastDomain: undefined,
  sidebarCollapsed: false,
  demoSidebarOpenSections: [], // Empty means all open by default
  menuIconOnly: false,
  menuAutoHide: false,
  menuDensity: 'regular',
  menuShowSectionCounts: true,
  layoutWidth: 'boxed',
  layoutPadding: 'md',
  presenceStatus: 'Online' as unknown as PresenceStatus,
  screenLocked: false, // Always defaults to unlocked on fresh load
  reducedMotion: false,
  highContrast: false,
  fontSize: 'regular',
  strongFocus: true,
  grayscaleMode: false,
  lineSpacing: 'regular',
  letterSpacing: 'normal',
  showIconLabels: true,
  enableTooltips: true,
  textToSpeech: false,
  uiSize: 'regular',
  lastSyncTime: undefined,
  lastModifiedTime: undefined,
};

export const usePreferencesStore = create<PreferencesStore>()(
  persist(
    (set, get) => ({
      ...DEFAULT_PREFERENCES,

      // Theme actions
      setColorSchemeMode: (mode) => set({ colorSchemeMode: mode }),
      setColorScheme: (key) => set({ colorSchemeKey: key }),
      setAccentColor: (color) => set({ accentColor: color }),

      // Language actions
      setLanguage: (lang) => {
        const language = SUPPORTED_LANGUAGES.find((l) => l.code === lang);
        set({
          language: lang,
          locale: `${lang}-${lang.toUpperCase()}`,
        });
        // Update document direction for RTL languages
        if (language) {
          document.documentElement.dir = language.direction;
        }
      },
      setLocale: (locale) => set({ locale }),

      // Regional actions
      setTimezone: (tz) => set({ timezone: tz }),
      setDateFormat: (format) => set({ dateFormat: format }),
      setTimeFormat: (format) => set({ timeFormat: format }),
      setDaylightSaving: (mode) => set({ daylightSaving: mode }),

      // Navigation actions
      setLastDomain: (domain) => set({ lastDomain: domain }),
      toggleSidebar: () => set((state) => ({ sidebarCollapsed: !state.sidebarCollapsed })),
      setSidebarCollapsed: (collapsed) => set({ sidebarCollapsed: collapsed }),
      setDemoSidebarOpenSections: (sections) => set({ demoSidebarOpenSections: sections }),
      toggleDemoSidebarSection: (sectionLabel) =>
        set((state) => {
          const sections = state.demoSidebarOpenSections;
          const isOpen = sections.length === 0 || !sections.includes(sectionLabel);
          if (sections.length === 0) {
            // First toggle - start tracking (close this section)
            return { demoSidebarOpenSections: [sectionLabel] };
          }
          return {
            demoSidebarOpenSections: isOpen
              ? [...sections, sectionLabel]
              : sections.filter((s) => s !== sectionLabel),
          };
        }),

      // Menu UI actions
      toggleMenuIconOnly: () => set((state) => ({ menuIconOnly: !state.menuIconOnly })),
      setMenuAutoHide: (autoHide) => set({ menuAutoHide: autoHide }),
      setMenuDensity: (density) => set({ menuDensity: density }),
      toggleMenuShowSectionCounts: () =>
        set((state) => ({ menuShowSectionCounts: !state.menuShowSectionCounts })),

      // Layout actions
      setLayoutWidth: (width) => set({ layoutWidth: width }),
      setLayoutPadding: (padding) => set({ layoutPadding: padding }),

      // Presence actions
      setPresenceStatus: (status) => set({ presenceStatus: status }),

      // Screen Lock actions
      setScreenLocked: (locked) => set({ screenLocked: locked }),

      // Accessibility actions
      setReducedMotion: (reduced) => set({ reducedMotion: reduced }),
      setHighContrast: (high) => set({ highContrast: high }),
      setStrongFocus: (strong) => set({ strongFocus: strong }),
      setFontSize: (size) => set({ fontSize: size }),
      setGrayscaleMode: (enabled) => set({ grayscaleMode: enabled }),
      setLineSpacing: (spacing) => set({ lineSpacing: spacing }),
      setLetterSpacing: (spacing) => set({ letterSpacing: spacing }),
      setShowIconLabels: (show) => set({ showIconLabels: show }),
      setEnableTooltips: (enabled) => set({ enableTooltips: enabled }),
      setTextToSpeech: (enabled) => set({ textToSpeech: enabled }),
      setUiSize: (size) => set({ uiSize: size }),

      // Utility actions
      resetPreferences: () => set(DEFAULT_PREFERENCES),
      setPreference: (key, value) => set({ [key]: value }),
      updatePreferences: (preferences) =>
        set((state) => ({
          ...state,
          ...preferences,
          lastModifiedTime: Math.floor(Date.now() / 1000),
        })),

      // Computed getters
      getColorScheme: () => {
        const { colorSchemeKey } = get();
        return colorSchemes.find((s) => s.key === colorSchemeKey) || defaultColorScheme;
      },
      getLanguage: () => {
        const { language } = get();
        return SUPPORTED_LANGUAGES.find((l) => l.code === language) || SUPPORTED_LANGUAGES[0];
      },
      getEffectiveColorMode: () => {
        const { colorSchemeMode } = get();
        if (colorSchemeMode === 'auto') {
          return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
        }
        return colorSchemeMode;
      },
    }),
    {
      name: 'ccms_preferences',
      storage: {
        getItem: (name) => {
          const value = encryptedStorage.getItem(name);
          return value ? JSON.parse(value) : null;
        },
        setItem: (name, value) => {
          encryptedStorage.setItem(name, JSON.stringify(value));
        },
        removeItem: (name) => {
          encryptedStorage.removeItem(name);
        },
      },
      partialize: (state) => {
        // Persist all state including screenLocked
        return { ...state };
      },
    }
  )
);

// Selectors
export const selectColorSchemeMode = (state: PreferencesStore) => state.colorSchemeMode;
export const selectColorSchemeKey = (state: PreferencesStore) => state.colorSchemeKey;
export const selectLanguage = (state: PreferencesStore) => state.language;
export const selectTimezone = (state: PreferencesStore) => state.timezone;
export const selectLastDomain = (state: PreferencesStore) => state.lastDomain;
export const selectSidebarCollapsed = (state: PreferencesStore) => state.sidebarCollapsed;

// Menu UI selectors
export const selectMenuIconOnly = (state: PreferencesStore) => state.menuIconOnly;
export const selectMenuAutoHide = (state: PreferencesStore) => state.menuAutoHide;
export const selectMenuDensity = (state: PreferencesStore) => state.menuDensity;
export const selectMenuShowSectionCounts = (state: PreferencesStore) =>
  state.menuShowSectionCounts;

// Layout selectors
export const selectLayoutWidth = (state: PreferencesStore) => state.layoutWidth;
export const selectLayoutPadding = (state: PreferencesStore) => state.layoutPadding;
